import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faChartPie, faUserSlash, faUsers
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Customer-Lifetime-Value-Process.png";
import vis from "../../assets/images/use-cases/Customer-Lifetime-Value-Dashboard.png";

const CustomerLifetimeValue = () => {
    return (
        <>
            <SEO
                title="Customer Lifetime Value Analytics: Predict and Reduce Churn | Nebulyx AI"
                description="Predict and reduce customer churn by understanding lifetime value drivers. Unify CRM platforms with customer and order data to calculate CLV and enable targeted retention strategies."
                keywords="AI digital twin, customer lifetime value, CLV, churn prediction, retention strategy, CRM analytics, customer analytics"
                canonicalUrl="https://nebulyx.ai/use-cases/customer-lifetime-value"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faChartPie}/>
                                            &nbsp;
                                            Retail
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How can we predict and reduce customer churn by understanding lifetime value drivers?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Unify CRM platforms (Zoho, Zendesk, Gong) with customer and order data to calculate Customer Lifetime Value through Average Order Value, Purchase Frequency, and Customer Lifespan metrics-enabling targeted retention strategies for high-value customers at risk of churning.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faUserSlash} className="stat-icon"/>
                                <span className="stat-value">35%</span>
                                <span className="stat-label">Churn Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">2.5x</span>
                                <span className="stat-label">CLV Accuracy</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faUsers} className="stat-icon"/>
                                <span className="stat-value">40%</span>
                                <span className="stat-label">Retention ROI</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Customer data siloed across CRM, support, and sales tools</li>
                                            <li>No unified view of customer lifetime value drivers</li>
                                            <li>Churn detected too late-after customers have already left</li>
                                            <li>Retention spend wasted on low-value or unlikely-to-churn customers</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Unified data pipeline from Zoho, Zendesk, and Gong</li>
                                            <li>Real-time CLV calculation from AOV, frequency, and lifespan</li>
                                            <li>Predictive churn scoring with early warning indicators</li>
                                            <li>Automated segmentation for targeted retention campaigns</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Marketing teams focus retention on highest-value at-risk customers</li>
                                            <li>Sales teams prioritise leads by predicted lifetime value</li>
                                            <li>Support teams see full customer context for better service</li>
                                            <li>Executives get real-time customer health dashboards</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connects CRM data, customer interactions, order history, and engagement signals into a unified Data Object Graph that powers predictive CLV and churn analytics across every customer touchpoint.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Ingest & Unify</strong>
                                    <span>Connect CRM, support, and sales data into unified customer profiles</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Calculate & Score</strong>
                                    <span>Derive AOV, purchase frequency, lifespan, and engagement metrics</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Predict & Segment</strong>
                                    <span>Model CLV and churn risk to segment customers for action</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Target & Retain</strong>
                                    <span>Trigger personalised retention campaigns for at-risk high-value customers</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from Zoho CRM, Zendesk support tickets, and Gong call analytics alongside
                                            customer and order data. Calculates key CLV components-Average Order Value, Purchase Frequency,
                                            and Customer Lifespan-to produce actionable lifetime value and churn predictions.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Customer Intelligence Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Unified interface showing customer segments by CLV and churn risk, individual customer
                                            health scores, engagement trends, and recommended retention actions.
                                            Marketing and sales teams can drill down into any segment or customer for targeted campaigns.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Zoho CRM Pipeline & Deals</li>
                                            <li>Zendesk Support Tickets</li>
                                            <li>Gong Call Recordings & Insights</li>
                                            <li>Customer Master Data</li>
                                            <li>Order History & Transactions</li>
                                            <li>Email & Marketing Engagement</li>
                                            <li>Website & Product Usage</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Customer Lifetime Value Calculator</li>
                                            <li>Churn Prediction Model</li>
                                            <li>Average Order Value Tracker</li>
                                            <li>Purchase Frequency Analyser</li>
                                            <li>Customer Lifespan Estimator</li>
                                            <li>Engagement Scoring Engine</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Use Cases Supported</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>High-Value Customer Retention</li>
                                            <li>Churn Early Warning Alerts</li>
                                            <li>Lead Prioritisation by CLV</li>
                                            <li>Campaign ROI Optimisation</li>
                                            <li>Customer Segment Analysis</li>
                                            <li>Revenue Forecasting</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling customer relationships as a <strong>Data Object Graph</strong>, we can connect
                                        CRM interactions, support tickets, sales calls, and order history into unified customer profiles.
                                        <strong> The CLV engine transforms this connected view into predictive lifetime value and churn scores</strong>
                                        {" "}-enabling marketing teams to focus retention spend on the customers who matter most,
                                        while sales teams prioritise leads most likely to become high-value long-term customers.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Predictive Retention</span>
                                        <span className="discovery-note">35% less churn, 40% better retention ROI</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Predict and Prevent Customer Churn?</h2>
                            <p>
                                If your customer data is siloed and churn is costing you revenue,
                                let's build your Customer Lifetime Value Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default CustomerLifetimeValue;